import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

@Directive({
  selector: '[appImageZoom]',
})
export class ImageZoomDirective implements OnChanges, OnDestroy {
  @Input('appImageZoom') imageUrl: string = '';
  @Input() zoomFactor: number = 2.5;
  @Input() lensSize: number = 160;

  private hostElement: HTMLImageElement;
  private imageContainer!: HTMLElement;
  private zoomPane!: HTMLElement;
  private zoomedContainer!: HTMLElement; // The new scaled-up container
  private lens!: HTMLElement;

  // Listeners
  private mouseEnterListener!: () => void;
  private mouseLeaveListener!: () => void;
  private mouseMoveListener!: (event?: MouseEvent) => void;
  private imageLoadListener!: () => void;

  constructor(
    private el: ElementRef<HTMLImageElement>,
    private renderer: Renderer2,
  ) {
    this.hostElement = this.el.nativeElement;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imageUrl'] && changes['imageUrl'].currentValue) {
      this.destroyZoom();
      this.imageLoadListener = this.renderer.listen(this.hostElement, 'load', () => {
        this.setupZoom();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyZoom();
  }

  private setupZoom(): void {
    if (!this.imageUrl || !this.hostElement.parentElement) return;

    this.imageContainer = this.renderer.parentNode(this.hostElement);
    this.renderer.addClass(this.imageContainer, 'image-zoom-container');

    this.createZoomPane();
    this.createLens();
    this.addEventListeners();
  }

  private createZoomPane(): void {
    // The zoomPane is the outer viewport
    this.zoomPane = this.renderer.createElement('div');
    this.renderer.addClass(this.zoomPane, 'zoom-pane');

    // The zoomedContainer is the inner element that gets scaled and moved
    this.zoomedContainer = this.renderer.createElement('div');
    this.renderer.setStyle(this.zoomedContainer, 'position', 'absolute');
    this.renderer.setStyle(this.zoomedContainer, 'background-image', `url(${this.imageUrl})`);
    this.renderer.setStyle(this.zoomedContainer, 'background-repeat', 'no-repeat');

    this.renderer.appendChild(this.zoomPane, this.zoomedContainer);
    this.renderer.appendChild(this.imageContainer, this.zoomPane);
  }

  private createLens(): void {
    this.lens = this.renderer.createElement('div');
    this.renderer.addClass(this.lens, 'zoom-lens');
    this.renderer.setStyle(this.lens, 'width', `${this.lensSize}px`);
    this.renderer.setStyle(this.lens, 'height', `${this.lensSize}px`);
    this.renderer.appendChild(this.imageContainer, this.lens);
  }

  private addEventListeners(): void {
    this.mouseEnterListener = this.renderer.listen(this.imageContainer, 'mouseenter', () =>
      this.showZoom(true),
    );
    this.mouseLeaveListener = this.renderer.listen(this.imageContainer, 'mouseleave', () =>
      this.showZoom(false),
    );
    this.mouseMoveListener = this.renderer.listen(this.imageContainer, 'mousemove', event =>
      this.onMouseMove(event),
    );
  }

  private showZoom(visible: boolean): void {
    const action = visible ? 'addClass' : 'removeClass';
    if (this.zoomPane) this.renderer[action](this.zoomPane, 'visible');
    if (this.lens) this.renderer[action](this.lens, 'visible');
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.imageContainer || !this.lens || !this.zoomPane || !this.zoomedContainer) return;

    // 1. Get dimensions of the container and the natural image
    const containerRect = this.imageContainer.getBoundingClientRect();
    const { naturalWidth, naturalHeight } = this.hostElement;
    const containerRatio = containerRect.width / containerRect.height;
    const imageRatio = naturalWidth / naturalHeight;

    // 2. Calculate the visual dimensions of the image within the container (respecting object-fit)
    let renderedWidth, renderedHeight, offsetX, offsetY;
    if (containerRatio > imageRatio) {
      renderedHeight = containerRect.height;
      renderedWidth = renderedHeight * imageRatio;
      offsetX = (containerRect.width - renderedWidth) / 2;
      offsetY = 0;
    } else {
      renderedWidth = containerRect.width;
      renderedHeight = renderedWidth / imageRatio;
      offsetX = 0;
      offsetY = (containerRect.height - renderedHeight) / 2;
    }

    // 3. Position the lens based on cursor, clamped to container bounds
    const mouseX = event.clientX - containerRect.left;
    const mouseY = event.clientY - containerRect.top;
    let lensX = mouseX - this.lensSize / 2;
    let lensY = mouseY - this.lensSize / 2;
    lensX = Math.max(0, Math.min(lensX, containerRect.width - this.lensSize));
    lensY = Math.max(0, Math.min(lensY, containerRect.height - this.lensSize));
    this.renderer.setStyle(this.lens, 'left', `${lensX}px`);
    this.renderer.setStyle(this.lens, 'top', `${lensY}px`);

    // 4. Configure the zoomedContainer to be a scaled-up version of the original container
    const zoomedContainerWidth = containerRect.width * this.zoomFactor;
    const zoomedContainerHeight = containerRect.height * this.zoomFactor;
    const originalBgColor = window.getComputedStyle(this.imageContainer).backgroundColor;

    this.renderer.setStyle(this.zoomedContainer, 'width', `${zoomedContainerWidth}px`);
    this.renderer.setStyle(this.zoomedContainer, 'height', `${zoomedContainerHeight}px`);
    this.renderer.setStyle(this.zoomedContainer, 'background-color', originalBgColor);

    // 5. Size and position the background image *within* the zoomedContainer
    const bgWidth = renderedWidth * this.zoomFactor;
    const bgHeight = renderedHeight * this.zoomFactor;
    const bgPosX = offsetX * this.zoomFactor;
    const bgPosY = offsetY * this.zoomFactor;
    this.renderer.setStyle(this.zoomedContainer, 'background-size', `${bgWidth}px ${bgHeight}px`);
    this.renderer.setStyle(this.zoomedContainer, 'background-position', `${bgPosX}px ${bgPosY}px`);

    // 6. Calculate the translation needed to move the zoomedContainer inside the zoomPane
    const percentX = lensX / (containerRect.width - this.lensSize);
    const percentY = lensY / (containerRect.height - this.lensSize);
    const translateX = -percentX * (zoomedContainerWidth - containerRect.width);
    const translateY = -percentY * (zoomedContainerHeight - containerRect.height);
    this.renderer.setStyle(
      this.zoomedContainer,
      'transform',
      `translate(${translateX}px, ${translateY}px)`,
    );
  }

  private destroyZoom(): void {
    if (this.mouseEnterListener) this.mouseEnterListener();
    if (this.mouseLeaveListener) this.mouseLeaveListener();
    if (this.mouseMoveListener) this.mouseMoveListener();
    if (this.imageLoadListener) this.imageLoadListener();

    if (this.imageContainer) {
      if (this.zoomPane) this.renderer.removeChild(this.imageContainer, this.zoomPane);
      if (this.lens) this.renderer.removeChild(this.imageContainer, this.lens);
      this.renderer.removeClass(this.imageContainer, 'image-zoom-container');
    }
  }
}
